/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useDispatch } from 'react-redux';

import moment from 'moment';

import { useRetracked } from 'js/lib/retracked';
import { useLocation, useNavigate } from 'js/lib/useRouter';
import { get } from 'js/lib/user';
import { humanizeLearningTime } from 'js/utils/DateTimeUtils';

import { Typography2 } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';
import type { EventData } from '@coursera/event-pulse/react';

import { CoachMode } from 'bundles/ai-coach-platform/constants';
import { useCoachTrackingContext } from 'bundles/ai-coach-platform/utils/tracking';
import { CoachNavigationContainer } from 'bundles/ai-course-coach/components/common';
import type { AiCoachNavigationButtonsFragment } from 'bundles/ai-course-coach/queries/__generated__/aiCoachNavigationButtonsFragment';
import { toggleChat } from 'bundles/ai-course-coach/store/actions';
import { generateCourseItemLink } from 'bundles/ai-course-coach/utils/links';
import type { RecommendedNavigationItem } from 'bundles/ai-course-coach/utils/types';

import _t from 'i18n!nls/learning-assistant-chat-ai';

type Props = { navigationButton: AiCoachNavigationButtonsFragment };

const styles = {
  recommendedNavContainer: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
    margin-top: var(--cds-spacing-200);
    margin-bottom: var(--cds-spacing-200);
  `,
  leadInText: css`
    color: var(--cds-color-grey-700);
  `,
};

export const RecommendedNavigation = (props: Props): React.ReactElement | null => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const trackRetracked = useRetracked();
  const track = useTracker();
  const trackingContext = useCoachTrackingContext()?.data;
  const location = useLocation().pathname;

  const { navigationButton } = props;

  // Initialize tracking data early with safe defaults
  const trackingData: EventData<'view_coach_recommended_navigation'> = {
    coachNavigationMessage: navigationButton.navigationLeadingMessage,
    coachNavigationButtonType: navigationButton.__typename ?? 'UNSUPPORTED_NAV_BUTTON_TYPE',
    coachNavigationTargetUrl: '', // Will be updated if valid
    coach: {
      mode: trackingContext?.mode ?? CoachMode.Basic,
    },
  };

  // Call hook unconditionally at the top level
  const linkVisibilityRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_coach_recommended_navigation',
    trackingData
  );

  const itemId = navigationButton.itemMetadata.itemId;
  const titleContent = navigationButton.itemMetadata.itemTitle;
  const courseSlug = navigationButton.itemMetadata.course.slug;
  const itemSlug = navigationButton.itemMetadata.itemSlug;
  const currentUser = get();

  let itemType: RecommendedNavigationItem;
  switch (navigationButton.itemMetadata.__typename) {
    case 'AiCoach_ReadingItemMetadata':
      itemType = 'supplement';
      break;
    case 'AiCoach_LectureItemMetadata':
      itemType = 'lecture';
      break;
    // case 'AiCoach_QuizItemMetadata':
    //   itemType = 'exam';
    //   break;
    // case 'AiCoach_AssignmentItemMetadata':
    //   itemType = 'staffGraded';
    //   break;
    default:
      itemType = null;
  }

  if (!itemType) {
    return null;
  }

  let leadInText;
  switch (navigationButton.navigationLeadingMessage) {
    case 'WHY_TAKE_COURSE_NAVIGATION':
      leadInText = _t(
        // eslint-disable-next-line no-restricted-syntax
        "Now that you're familiar with the benefits of this course, let's move forward with your learning."
      );
      break;

    case 'HELP_GET_STARTED_WITH_COURSE_NAVIGATION':
      leadInText = _t("With these tips, you're ready for what's next. Let's dive into the upcoming lesson together.");
      break;

    default:
      leadInText = null;
  }

  let buttonText;
  switch (navigationButton.__typename) {
    case 'AiCoach_GoToFirstItemNavigationButton':
    default:
      buttonText = _t('Start learning');
      break;
  }

  let metadata: string[];
  switch (navigationButton.itemMetadata.__typename) {
    case 'AiCoach_ReadingItemMetadata':
      metadata = [humanizeLearningTime(moment.duration(navigationButton.itemMetadata.readingDuration))];
      break;
    case 'AiCoach_LectureItemMetadata':
      metadata = [humanizeLearningTime(moment.duration(navigationButton.itemMetadata.lectureDuration))];
      break;
    // case 'AiCoach_QuizItemMetadata':
    //   metadata = [_t('#{numQuestions}', { numQuestions: navigationButton.itemMetadata.numQuestions.toString() })];
    //   break;
    // case 'AiCoach_AssignmentItemMetadata':
    //   metadata = [_t('#{numQuestions}', { numQuestions: navigationButton.itemMetadata.numQuestions.toString() })];
    //   break;
    default:
      metadata = [];
  }

  const courseItemLink = generateCourseItemLink({ itemType, courseSlug, itemSlug, itemId });

  // Update tracking data URL now that we have valid data
  trackingData.coachNavigationTargetUrl = courseItemLink;

  const handleNavButtonClick = () => {
    // V2 eventing
    trackRetracked({
      trackingData: {
        userId: currentUser.id,
        userLocale: currentUser.locale,
        timestamp: Date.now(),
        navMessage: navigationButton.navigationLeadingMessage,
        navButtonType: navigationButton.__typename,
        navTargetUrl: courseItemLink,
        referrerUrl: location,
      },
      trackingName: 'ai_course_coach_recommended_navigation',
      action: `click`,
    });

    // V3 eventing
    track('click_coach_recommended_navigation', trackingData);

    dispatch(toggleChat());
    navigate(courseItemLink, { replace: false });
  };

  return (
    <div css={styles.recommendedNavContainer} ref={linkVisibilityRef}>
      <Typography2 variant="bodyPrimary" component="p" css={styles.leadInText}>
        <strong>{leadInText}</strong>
      </Typography2>

      <CoachNavigationContainer
        itemId={itemId}
        title={{ content: titleContent }}
        buttonText={buttonText}
        itemType={itemType}
        metadata={metadata}
        handleNavButtonClick={handleNavButtonClick}
      />
    </div>
  );
};

export default RecommendedNavigation;
